import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import api, { apiError } from "@/lib/api";
import { QRBox } from "@/components/QRBox";
import { ArrowLeft, Play, Square, Loader2, RefreshCw, Trophy } from "lucide-react";
import { toast } from "sonner";

export default function PollDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/polls/${id}`).then((r) => setD(r.data)).catch(() => toast.error("Not found")), [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    setBusy(true);
    try { await api.put(`/polls/${id}`, { status }); toast.success(status === "open" ? "Undian dibuka" : "Undian ditutup"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (!d) return <Layout title="Undian"><div className="flex items-center gap-2 text-slate"><Loader2 className="animate-spin" size={18} />Loading…</div></Layout>;
  const p = d.poll;
  const maxVotes = Math.max(1, ...d.results.map((r) => r.votes));
  const turnout = d.eligible ? Math.round((d.total_voters / d.eligible) * 100) : 0;

  return (
    <Layout title={p.title} subtitle={p.description || `Pilih ${p.max_choices} · ${p.candidates.length} calon`}
      actions={
        <>
          <button onClick={() => nav("/voting")} className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><ArrowLeft size={15} /> Kembali</button>
          <button onClick={load} className="flex items-center gap-1.5 bg-transparent border border-ink/20 text-ink hover:bg-ink/5 px-3 py-2 rounded-sm text-sm transition-colors"><RefreshCw size={15} /> </button>
          {p.status !== "open" ? (
            <button onClick={() => setStatus("open")} disabled={busy} data-testid="open-poll-btn" className="flex items-center gap-1.5 bg-positive text-white hover:bg-positive/90 px-3 py-2 rounded-sm text-sm transition-colors"><Play size={15} /> Buka Undian</button>
          ) : (
            <button onClick={() => setStatus("closed")} disabled={busy} data-testid="close-poll-btn" className="flex items-center gap-1.5 bg-brick text-white hover:bg-brick/90 px-3 py-2 rounded-sm text-sm transition-colors"><Square size={15} /> Tutup Undian</button>
          )}
        </>
      }
    >
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-hairline p-4 rounded-sm"><div className="text-xs uppercase tracking-wider text-slate">Status</div><div className={`font-serif text-2xl mt-1 capitalize ${p.status === "open" ? "text-positive" : p.status === "closed" ? "text-brick" : "text-slate"}`}>{p.status}</div></div>
            <div className="bg-white border border-hairline p-4 rounded-sm"><div className="text-xs uppercase tracking-wider text-slate">Mengundi</div><div className="font-serif text-2xl mt-1 text-ink">{d.total_voters}</div></div>
            <div className="bg-white border border-hairline p-4 rounded-sm"><div className="text-xs uppercase tracking-wider text-slate">Peratus / Turnout</div><div className="font-serif text-2xl mt-1 text-ink">{turnout}%</div></div>
          </div>

          <div className="bg-white border border-hairline rounded-sm">
            <div className="px-6 py-4 border-b border-hairline flex items-center gap-2"><Trophy size={18} className="text-brass" /><h3 className="font-serif text-lg text-ink">Keputusan Langsung / Live Results</h3></div>
            <div className="p-6 space-y-4">
              {d.results.map((r, i) => (
                <div key={r.id} data-testid={`result-${r.id}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-ink flex items-center gap-2">
                      {r.photo ? <img src={r.photo} alt="" className="w-7 h-7 rounded-full object-cover border border-hairline" /> : <div className="w-7 h-7 rounded-full bg-ink/10 flex items-center justify-center text-[11px] text-ink font-serif">{r.name?.[0]}</div>}
                      {i === 0 && r.votes > 0 && <Trophy size={14} className="text-brass" />}
                      <span>{r.name}{r.info ? <span className="text-slate text-xs ml-1">· {r.info}</span> : ""}{(r.pencadang || r.penyokong) ? <span className="text-slate/60 text-[10px] block">{r.pencadang ? `Pencadang: ${r.pencadang}` : ""}{r.pencadang && r.penyokong ? " · " : ""}{r.penyokong ? `Penyokong: ${r.penyokong}` : ""}</span> : ""}</span>
                    </span>
                    <span className="font-mono text-ink">{r.votes}</span>
                  </div>
                  <div className="h-2.5 bg-paper rounded-full overflow-hidden">
                    <div className={`h-full transition-all ${i === 0 && r.votes > 0 ? "bg-brass" : "bg-ink/40"}`} style={{ width: `${(r.votes / maxVotes) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <QRBox url={d.vote_link} label="Link/QR Undian / Voting" testid="vote-qrbox" />
          <div className="bg-brass/5 border border-brass/20 rounded-sm p-4 text-xs text-slate">
            {p.status === "draft" && "Klik \"Buka Undian\" untuk mula menerima undi. / Open the poll to start accepting votes."}
            {p.status === "open" && "Undian sedang dibuka. Kongsi QR/link kepada ahli. Setiap ahli sahkan No. Ahli + 4 digit IC, satu undi sahaja."}
            {p.status === "closed" && "Undian telah ditutup. Keputusan adalah muktamad."}
          </div>
        </div>
      </div>
    </Layout>
  );
}
